'use client';

import { useState, useEffect, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Milestone {
  year: string;
  title: string;
  description: string;
  scope: string[];
  contractor: string;
  amount: string;
  icon: string;
}

const CompanyIntroduction = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [counters, setCounters] = useState({ projects: 0, clients: 0, employees: 0 });
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  const milestones: Milestone[] = [
    {
      year: 'May 2017 — August 2019',
      title: 'BIOSAFETY LEVEL-3 LABORATORY / CDC',
      description: 'Design, procurement, installation, commissioning with integrated automation, HVAC & specialized clean power',
      scope: ['Design, procurement, installation, commissioning', 'Integrated automation, HVAC & specialized clean power'],
      contractor: 'University of Maryland',
      amount: 'US$ 900,000',
      icon: 'BeakerIcon'
    },
    {
      year: '2020–2021',
      title: 'CBK Pension Towers',
      description: '2 synchronised 500kVA generators, 30kW hybrid solar system, smart metering & power stabilization',
      scope: ['2 synchronised 500kVA generators', '30kW hybrid solar system', 'Smart metering & power stabilization'],
      contractor: 'China Wu Yi (via ICOM Engineering)',
      amount: 'US$ 1,200,000',
      icon: 'BuildingOfficeIcon'
    },
    {
      year: 'Dec 2020 – March 2021',
      title: 'KRIL Hospital by AAR',
      description: 'Online UPS 400kVA + 200kVA redundancy, cooling & clean power distribution',
      scope: ['Online UPS 400kVA + 200kVA redundancy', 'Cooling & clean power distribution'],
      contractor: 'China State Construction Engineering Corporation',
      amount: 'US$ 800,000',
      icon: 'BuildingOffice2Icon'
    },
    {
      year: '2020 - May 2021',
      title: 'Infinity Industrial Park Substation',
      description: '20MVA 66/11kV industrial grid with smart utilities integration',
      scope: ['20MVA 66/11kV industrial grid', 'Smart utilities integration'],
      contractor: 'Kenya Power and Lighting Company (KPLC)',
      amount: 'US$ 500,000',
      icon: 'BoltIcon'
    },
    {
      year: 'Feb 2022 - Dec 2023',
      title: 'Amahoro National Stadium - Kigali',
      description: 'Supply & installation of earthing system, lightning protection system, electrical safety supervision',
      scope: ['Supply & installation of earthing system', 'Lightning protection system', 'Electrical safety supervision'],
      contractor: 'SUMMA Construction Inc.',
      amount: 'US$ 1,500,000',
      icon: 'HomeModernIcon'
    },
    {
      year: 'May 2024 - Aug 2025',
      title: 'North Substation - Bugesera International Airport',
      description: 'Installation, testing and commissioning with full electrical grounding and protection system',
      scope: ['Installation, testing and commissioning', 'Full electrical grounding and protection system'],
      contractor: 'Mota-Engil Africa (Partner: Mothada Group)',
      amount: 'US$ 800,000',
      icon: 'BuildingLibraryIcon'
    }
  ];

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (!isHydrated) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [isHydrated]);

  useEffect(() => {
    if (!isVisible || !isHydrated) return;

    const targets = { projects: 500, clients: 150, employees: 200 };
    const duration = 2000;
    const steps = 60;
    const interval = duration / steps;

    let step = 0;
    const timer = setInterval(() => {
      step++;
      const progress = step / steps;

      setCounters({
        projects: Math.floor(targets.projects * progress),
        clients: Math.floor(targets.clients * progress),
        employees: Math.floor(targets.employees * progress)
      });

      if (step >= steps) {
        clearInterval(timer);
        setCounters(targets);
      }
    }, interval);

    return () => clearInterval(timer);
  }, [isVisible, isHydrated]);

  return (
    <section id="about" ref={sectionRef} className="py-20 bg-surface">
      <div className="container mx-auto px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="font-body text-xl text-muted-foreground max-w-3xl mx-auto">
              Since our founding in 2017, we've grown to become a trusted EPC contractor delivering excellence in electrical and mechanical engineering solutions across Rwanda and Kenya
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            <div className="bg-card rounded-lg p-8 shadow-card text-center transform hover:scale-105 transition-all duration-250">
              <Icon name="BriefcaseIcon" size={48} variant="solid" className="text-accent mx-auto mb-4" />
              <div className="font-headline text-5xl font-bold text-primary mb-2">
                {isHydrated ? `${counters.projects}+` : '500+'}
              </div>
              <p className="font-body text-muted-foreground">Projects Completed</p>
            </div>

            <div className="bg-card rounded-lg p-8 shadow-card text-center transform hover:scale-105 transition-all duration-250">
              <Icon name="UserGroupIcon" size={48} variant="solid" className="text-accent mx-auto mb-4" />
              <div className="font-headline text-5xl font-bold text-primary mb-2">
                {isHydrated ? `${counters.clients}+` : '150+'}
              </div>
              <p className="font-body text-muted-foreground">Satisfied Clients</p>
            </div>

            <div className="bg-card rounded-lg p-8 shadow-card text-center transform hover:scale-105 transition-all duration-250">
              <Icon name="UsersIcon" size={48} variant="solid" className="text-accent mx-auto mb-4" />
              <div className="font-headline text-5xl font-bold text-primary mb-2">
                {isHydrated ? `${counters.employees}+` : '200+'}
              </div>
              <p className="font-body text-muted-foreground">Expert Engineers</p>
            </div>
          </div>

          <div className="bg-card rounded-lg p-8 shadow-card">
            <h3 className="font-headline text-2xl font-bold text-primary mb-8 text-center">Our Growth Journey</h3>
            <div className="relative">
              <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-border hidden md:block" />
              
              <div className="space-y-8">
                {milestones.map((milestone, index) => (
                  <div
                    key={index}
                    className={`flex flex-col md:flex-row items-start gap-6 ${
                      index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                  >
                    <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                      <div className="bg-surface rounded-lg p-6">
                        <div className="font-headline text-lg font-bold text-accent mb-2">{milestone.year}</div>
                        <h4 className="font-headline text-xl font-semibold text-primary mb-3">{milestone.title}</h4>
                        <p className="font-body text-muted-foreground mb-4">{milestone.description}</p>
                        <div className="space-y-2 mb-4">
                          <p className="font-body text-sm text-muted-foreground">
                            <span className="font-semibold text-primary">Main Contractor:</span> {milestone.contractor}
                          </p>
                          <p className="font-body text-sm font-semibold text-accent">
                            Contract Amount: {milestone.amount}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="relative z-10 bg-primary rounded-full p-4 shadow-lg flex-shrink-0">
                      <Icon name={milestone.icon as any} size={32} variant="solid" className="text-white" />
                    </div>

                    <div className="flex-1 hidden md:block" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CompanyIntroduction;